import { captureException } from '@sentry/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { OrderModel } from '@merchant-clients/order';

import { usePrinterContext } from './printer-context';
import { usePrintedOrdersDb } from './use-printed-orders-db';
import { useSeenOrdersDb } from './use-seen-orders-db';

export interface UseAutomaticPrintQueueValue {
  queueOrderForPrinting(order: OrderModel): void;
  queueOrdersForPrinting(orders: OrderModel[]): void;
}

export const useAutomaticPrintQueue = (): UseAutomaticPrintQueueValue => {
  const printedOrdersDb = usePrintedOrdersDb();
  const seenOrdersDb = useSeenOrdersDb();
  const { printMerchantOrderAction, automaticPrinting } = usePrinterContext();

  const [queue, setQueue] = useState<OrderModel[]>([]);

  /**
   * Tracks order IDs that have been offered to this queue, regardless of
   * whether printing was enabled at the time. Initialized from SQLite on
   * mount so that the seen state survives app restarts. This prevents orders
   * that arrived while printing was disabled — including across restarts —
   * from being silently printed when the user later enables automatic printing.
   */
  const seenOrderIds = useRef(new Set<string>());

  /**
   * Whether seenOrderIds has been populated from SQLite. Orders that arrive
   * before initialization is complete are buffered so they are not processed
   * against an empty seen-set — which would cause previously-seen orders to
   * be treated as new and printed retroactively (e.g. after connecting a
   * printer and navigating back to the orders view).
   */
  const [seenOrdersInitialized, setSeenOrdersInitialized] = useState(false);

  /**
   * Orders buffered while seenOrderIds is still loading from SQLite.
   * Flushed via the effect below once initialization completes.
   */
  const bufferedOrders = useRef<OrderModel[]>([]);

  useEffect(() => {
    seenOrdersDb.getSeenOrderIds().then((ids) => {
      ids.forEach((id) => {
        seenOrderIds.current.add(id);
      });
      setSeenOrdersInitialized(true);
    });
  }, [seenOrdersDb.getSeenOrderIds]);

  useEffect(() => {
    if (printMerchantOrderAction.isRunning) {
      // We are already printing, waiting for printer to finish
      return;
    }

    if (queue.length === 0) {
      return;
    }

    // Pop first item in the queue. The state update will trigger a new
    // effect call that will most likely be ignored since at that time
    // we are in the process of printing.
    const [orderToPrint] = queue;
    // This effect is the queue's worker: it pops one order per run, and the
    // state change drives the next run. Reworking it needs device testing.
    setQueue((prev) => {
      // The queue is highly likely to have multiple state operations
      // queued up since multiple things are calling it that are also
      // async so we take that into account here by not using the
      // queue variable directly.
      return prev.slice(1);
    });

    if (!orderToPrint) {
      return;
    }

    printedOrdersDb
      .isOrderPrinted(orderToPrint.id)
      .then(async (isAlreadyPrinted) => {
        if (isAlreadyPrinted) {
          return;
        }

        try {
          // When run finishes the effect will be triggered again since the
          // isRunning state will change.
          const printResult = await printMerchantOrderAction.run(orderToPrint);

          if (printResult.result === 'SUCCESS') {
            // Since addOrder performs async calls and useEffect can be triggered again
            // before addOrder has finished code from this point and onwards must take
            // into account that state etc. is not guaranteed to be up to date.
            await printedOrdersDb.addOrder(orderToPrint.id);
          }
        } catch (error) {
          console.error(error);

          captureException(error, {
            level: 'error',
            extra: {
              message: `Failed to print order ${orderToPrint.id} from automatic print queue`,
            },
          });
        }
      });
  }, [
    queue,
    printMerchantOrderAction.isRunning,
    printMerchantOrderAction.run,
    printedOrdersDb.isOrderPrinted,
    printedOrdersDb.addOrder,
  ]);

  /**
   * Core logic: filters out already-seen orders, marks the rest as seen, and
   * enqueues them for printing if automatic printing is enabled. Callers are
   * responsible for ensuring seenOrderIds has been initialized before calling
   * this function.
   */
  const processOrders = useCallback(
    (orders: OrderModel[]) => {
      // Only process orders that haven't been seen in this session yet.
      // Orders offered while printing was disabled are tracked here so that
      // they won't suddenly print if the user later enables automatic printing.
      // The in-memory set is the fast-path; SQLite backs it across restarts.
      const unseenOrders = orders.filter(
        (order) => !seenOrderIds.current.has(order.id),
      );

      if (unseenOrders.length === 0) {
        return;
      }

      unseenOrders.forEach((order) => {
        seenOrderIds.current.add(order.id);
      });

      seenOrdersDb
        .addOrders(unseenOrders.map((order) => order.id))
        .catch((error) => {
          console.error('Failed to persist seen order IDs:', error);

          captureException(error, {
            level: 'error',
            extra: { message: 'Failed to persist seen order IDs' },
          });
        });

      if (automaticPrinting === 'PRINT_AT_CONFIRMATION') {
        setQueue((currentQueue) => [...currentQueue, ...unseenOrders]);
      }
    },
    [automaticPrinting, seenOrdersDb],
  );

  // Once seenOrderIds is initialized from SQLite, flush any orders that
  // arrived before initialization completed.
  useEffect(() => {
    if (!seenOrdersInitialized || bufferedOrders.current.length === 0) {
      return;
    }
    const toProcess = bufferedOrders.current;
    bufferedOrders.current = [];
    processOrders(toProcess);
  }, [seenOrdersInitialized, processOrders]);

  const queueOrdersForPrinting = useCallback(
    (orders: OrderModel[]) => {
      if (!seenOrdersInitialized) {
        // Buffer orders until seenOrderIds has been loaded from SQLite.
        // Processing them now against an empty seen-set would incorrectly
        // treat previously-accepted orders as new and print them retroactively
        // (e.g. when the user connects a printer after the component remounts).
        bufferedOrders.current = [...bufferedOrders.current, ...orders];
        return;
      }

      processOrders(orders);
    },
    [seenOrdersInitialized, processOrders],
  );

  const queueOrderForPrinting = useCallback(
    (order: OrderModel) => {
      queueOrdersForPrinting([order]);
    },
    [queueOrdersForPrinting],
  );

  return useMemo(
    (): UseAutomaticPrintQueueValue => ({
      queueOrderForPrinting,
      queueOrdersForPrinting,
    }),
    [queueOrderForPrinting, queueOrdersForPrinting],
  );
};
